import { pick, isNil, omit, map, path as rPath } from 'ramda';

import { distance as getGeoDistance } from '@utils/geodesy.ts';

import { calculateAdjacent, encode } from '../../../src/extModules/geohasher.ts';
import MinPriorityQueue from './minPriorityQueue.ts';
import type {
  AnyRecord,
  Endpoint,
  GeoDb,
  NavEdge,
  NavNode,
  NodeId,
  NodesMap,
  RouteOptions,
  SecurityLanesMap,
  SecurityWaitTimes,
} from './types.ts';

const DEFAULT_WALKING_SPEED_M_PER_MIN = 60;
// todo a flat penalty is a stand-in for "impassable". Replace with a real
// unreachable-destination path once consumers can handle it (see cost-table task).
const CLOSED_CHECKPOINT_WAIT_MINUTES = 9999;

/**
 * Time to traverse an edge: its live dynamic wait (currently only security queues)
 * when one is known, otherwise its static transitTime.
 *
 * These aren't additive: a checkpoint edge's static transitTime is itself a baseline
 * wait estimate (e.g. a lane's defaultQueueTime), not a distance separate from the
 * queue - so a live reading replaces it rather than stacking on top.
 *
 * This is the single definition of "how long does this edge take". Dijkstra, path
 * totals, and per-waypoint ETAs all go through it so that the ordering of search
 * results and the route times we display cannot drift apart.
 *
 * @param {NavEdge} edge
 * @returns {number} minutes
 */
export function getEdgeTraversalTime(edge: NavEdge) {
  return edge.securityWaitTimes ? edge.waitTime : edge.transitTime;
}

/**
 * @typedef NavNode
 * @property {number} ordinal
 * @property {Array.<NavEdge>} edges
 * @property {string} id
 * @property {number} lat
 * @property {number} lng
 * @property {string} floorId
 * @property {structureId} structureId
 *
 * @typedef NavEdge
 * @property {string} dst - id of node at edge end
 * @property {string} src - id of node at edge start
 * @property {number} distance
 * @property {string|undefined} o - id of security lane POI associated with this edge
 * @property {CurvedPath|null} path - list of Bezier points if edge represents curve
 * @property {boolean} isAccessible
 * @property {boolean} isDriveway - true if edge points to POI, false if edge just connects other edges
 * @property {number} transitTime - static time to traverse the edge; for checkpoint edges this is
 *   itself a baseline wait estimate, superseded by waitTime once live data is known
 * @property {string} type
 * @property {number} waitTime - live dynamic wait to traverse the edge (e.g. security queue),
 *   replacing transitTime when securityWaitTimes is set; 0 if no live data
 *
 * @param {RawNavGraph} data
 * @param {function} floorIdToOrdinal
 * @param {function} floorIdToStructureId
 * @param {Array.<SecurityLane>} securityLanesMap - list of security lanes
 * @return {Object}
 */
// todo remove dead commented code
export function createNavGraph(
  data: AnyRecord,
  floorIdToOrdinal: (floorId: string) => number,
  floorIdToStructureId: (floorId: string) => string,
  securityLanesMap: SecurityLanesMap,
) {
  const nodes: NodesMap = {};
  const geoDb: GeoDb = {};
  const nodesToAvoid = new Set<string>();

  data.nodes.forEach((nodeData: AnyRecord) => {
    const ordinal = floorIdToOrdinal(nodeData.floorId);
    const structureId = floorIdToStructureId(nodeData.floorId);
    const node = {
      ...pick(['id', 'lat', 'lng', 'floorId'], nodeData),
      edges: [],
      ordinal,
      structureId,
    };
    addNode(node);
  });

  data.edges.forEach((ed: AnyRecord) => nodes[ed.s].edges.push(createEdge(ed, nodes)));

  function addNode(node: NavNode) {
    const largeGeo = node.floorId + ':' + encode(node.lat, node.lng).substr(0, 7);
    const mediumGeo = node.floorId + ':' + encode(node.lat, node.lng).substr(0, 8);

    if (!geoDb[largeGeo]) {
      geoDb[largeGeo] = [];
    }

    geoDb[largeGeo].push(node);

    if (!geoDb[mediumGeo]) {
      geoDb[mediumGeo] = [];
    }

    geoDb[mediumGeo].push(node);

    nodes[node.id] = node;
  }

  function createEdge(data: AnyRecord, nodes: NodesMap): NavEdge {
    const type = getEdgeType(data);
    const typeLower = type.toLowerCase();
    // All edge types are accessible except Escalator and regular Stairs (AccessibleStairs are accessible)
    const isAccessible = typeLower !== 'escalator' && typeLower !== 'stairs';
    // todo consider calculating edge distance with 'path' differently
    const distance = distanceBetweenNodes(data.s, data.d, nodes);
    const transitTime = data.l || distance / DEFAULT_WALKING_SPEED_M_PER_MIN;

    const buildCurvedPath = (points: AnyRecord[]) =>
      points.map((point: AnyRecord) => {
        return {
          start: { lat: point.s[0], lng: point.s[1] },
          out: { lat: point.o[0], lng: point.o[1] },
          in: { lat: point.i[0], lng: point.i[1] },
          end: { lat: point.e[0], lng: point.e[1] },
        };
      });

    const path = data.p ? buildCurvedPath(data.p) : null;

    return {
      distance,
      dst: data.d,
      o: data.o,
      isAccessible,
      isDriveway: !isNil(data.h) && !data.h,
      src: data.s,
      transitTime,
      type,
      path,
      waitTime: 0, // populated by updateWithSecurityWaitTime
    };
  }

  function getEdgeType(data: AnyRecord) {
    if (data.x) {
      return 'Security Checkpoint';
    }
    if (data.t === '') {
      return 'Ground';
    }
    return data.t;
  }

  const findClosestNode = (endpoint: Endpoint) => {
    if (endpoint.floorId === undefined && endpoint.ordinal === undefined) {
      // one of these must be present
      throw Error('Endpoint specified in findRoute without floorId nor an ordinal');
    }
    const lat = endpoint.lat || endpoint.latitude; // handle bluedot location
    const lng = endpoint.lng || endpoint.longitude; // handle bluedot location
    return endpoint.floorId
      ? findClosestNodeByFloor(endpoint.floorId, lat, lng, geoDb, nodes)
      : findClosestNodeByOrdinal(endpoint.ordinal, lat, lng, nodes);
  };

  function findShortestPathEntry(start: Endpoint, end: Endpoint, nodes: NodesMap, options: RouteOptions = {}) {
    const startNode = findClosestNode(start);
    const endNode = findClosestNode(end);

    // This code section does improve performance by about 10-20%, but comes at a cost
    // of making the caching unusable in some cases
    // if (start.structureId === end.structureId) {
    //   options.minOrd = Math.min(start.ordinal, end.ordinal)
    //   options.maxOrd = Math.max(start.ordinal, end.ordinal)
    //   options.structureId = start.structureId
    // }
    return findShortestPath(startNode, endNode, nodes, nodesToAvoid, securityLanesMap, options);
  }

  /**
   *
   * @param {Array.<string>} nodes - Array of nodes to avoid
   */
  function updateNodesToAvoid(nodes: string[]) {
    // We want to replace entire list when we get new nodes, not just add
    nodesToAvoid.clear();
    nodes.forEach(n => nodesToAvoid.add(n));
  }
  /**
   * @param  {Endpoint} start - start endpoint
   * @param  {Array.<Endpoint>} destArray - list of destinations
   * @param  {RouteOptions} options extra options (such as requireAccessibility)
   * @returns {Array.<Array.<NavNode>>} array of routes corresponding to destinations specified
   */
  function findAllShortestPaths(start: Endpoint, destArray: Endpoint[], options: RouteOptions) {
    const startNode = findClosestNode(start);
    const destNodeArray = destArray.map(dest => findClosestNode(dest));
    if (!startNode || !destNodeArray.length) {
      return [];
    }
    return findAllShortestPathsImpl(startNode, destNodeArray, nodes, nodesToAvoid, securityLanesMap, options);
  }

  /**
   * Applies dynamic security queue data to the graph's edges.
   *
   * The wait is stored on the edge as data rather than being applied while
   * traversing, so every edge has a defined value regardless of which paths a given
   * Dijkstra run happens to explore, and values are cleared when a checkpoint drops
   * out of the feed instead of lingering.
   *
   * @param {Object.<string, SecurityWaitTime>} waitTimesData - map of POI id to security wait time
   */
  function updateWithSecurityWaitTime(waitTimesData: SecurityWaitTimes) {
    const waitTimes: SecurityWaitTimes = map(omit(['lastUpdated']), waitTimesData);

    forEachEdge(edge => {
      const dynamicData = edge.o === undefined ? undefined : waitTimes[edge.o];

      if (!dynamicData) {
        edge.waitTime = 0;
        delete edge.securityWaitTimes;
        return;
      }

      edge.securityWaitTimes = dynamicData;
      edge.waitTime = dynamicData.isTemporarilyClosed ? CLOSED_CHECKPOINT_WAIT_MINUTES : (dynamicData.queueTime ?? 0);
    });

    clearCache();
  }

  function forEachEdge(fn: (edge: NavEdge) => void) {
    for (const node of Object.values(nodes)) {
      for (const edge of node.edges) {
        fn(edge);
      }
    }
  }

  return {
    _nodes: nodes,
    _geoDb: geoDb,
    _nodesToAvoid: nodesToAvoid,
    addNodesToAvoid: (nodes: string[]) => updateNodesToAvoid(nodes),
    findClosestNode: (floorId: string, lat: number, lng: number) =>
      findClosestNodeByFloor(floorId, lat, lng, geoDb, nodes),
    findShortestPath: (start: Endpoint, end: Endpoint, options: RouteOptions) =>
      findShortestPathEntry(start, end, nodes, options),
    findAllShortestPaths,
    floorIdToOrdinal, // todo lets get rid of this...
    floorIdToStructureId, // todo lets get rid of this...,
    updateWithSecurityWaitTime,
    clearCache,
  };
}

function distanceBetweenNodes(n1: string, n2: string, nodes: NodesMap) {
  const node1 = nodes[n1];
  const node2 = nodes[n2];
  const distance = getGeoDistance(node1.lat, node1.lng, node2.lat, node2.lng);
  return distance;
}

/**
 * @param {Object.<Node>} start - a node in the navGraph to start on
 * @param {Array.<Node>} destinations - array of nodes to find path to
 * @param {Object.<string, NavNode>} nodes - dictionary of nodes by id
 * @param {Set.<string>} nodesToAvoid - set of nodes to avoid
 * @param {Object.<string, SecurityLane>} securityLanesMap - map of POI id to security lane
 * @param  {RouteOptions} [options={}] extra options (such as requireAccessibility)
 * @returns {Array.<Array.<Node>>} list of shortest path to each destination
 */
function findAllShortestPathsImpl(
  start: NavNode,
  destinations: NavNode[],
  nodes: NodesMap,
  nodesToAvoid: Set<string>,
  securityLanesMap: SecurityLanesMap = {},
  options: RouteOptions = {},
) {
  // const previous = findPaths(start, start, nodes, options)

  // const backtrackPath = node => buildBacktrackPath(nodes, previous, node)
  // const poiNodeTuples = Array.from(destinations.entries())

  // const poiPathTuples = poiNodeTuples.map(([poi, node]) => [poi, backtrackPath(node)])
  // return new Map(poiPathTuples)
  return destinations.map(d => {
    try {
      return findShortestPath(start, d, nodes, nodesToAvoid, securityLanesMap, options);
    } catch {
      return null;
    }
  });
}

let cost: Record<string, number>;
let prev: Record<string, NavNode | undefined>;
let visited: Record<string, boolean>;
let visitQueue: MinPriorityQueue;
let lastStartId: NodeId | null;
let lastOptionsStr: string | AnyRecord;

const clearCache = () => {
  cost = {};
  prev = {};
  visited = {};
  visitQueue = new MinPriorityQueue();
  lastStartId = null;
  lastOptionsStr = {};
};

/**
 * Finds the shortest path between two nodes in the navigation graph using Dijkstra's algorithm.
 * Supports caching for repeated queries with the same start node and options.
 *
 * NOTE: export just for testing
 *
 * @param  {Object<Node>} start a node in the navGraph to start on
 * @param  {Object<Node>} end a node in the navGraph to find path to
 * @param  {Object.<string, Node>} nodes dictionary of nodes by id
 * @param {Set.<string>} nodesToAvoid - set of nodes to avoid
 * @param {Object.<string, SecurityLane>} securityLanesMap - map of POI id to security lane
 * @param  {RouteOptions} options={} extra options (such as requireAccessibility)
 * @returns {Array.<Node>} an array of nodes that represent the shortest route from start to end. null if no route exists.
 */
export function findShortestPath(
  start: NavNode,
  end: NavNode,
  nodes: NodesMap,
  nodesToAvoid: Set<NodeId>,
  securityLanesMap: SecurityLanesMap = {},
  options: RouteOptions = {},
): NavNode[] | null {
  if (start.id !== lastStartId || lastOptionsStr !== JSON.stringify(options)) {
    clearCache();
    visitQueue.offerWithPriority(start.id, 0);
    cost[start.id] = 0;
    visited[start.id] = true;

    lastStartId = start.id;
    lastOptionsStr = JSON.stringify(options);
  }

  // continue crawling paths - but stop once we found destination
  while (!visitQueue.isEmpty() && !visited[end.id]) {
    const nextNodeId = visitQueue.poll();
    if (nextNodeId == null) {
      break;
    }
    const node = nodes[nextNodeId]; // pop
    const ccost = cost[node.id]; // current cost to this node
    for (let ei = 0; ei < node.edges.length; ei++) {
      const e = node.edges[ei]; // next edge from this node

      if (nodesToAvoid.size > 0 && nodesToAvoid.has(e.dst)) {
        continue;
      }
      if (visited[e.dst]) {
        continue;
      }

      if (options.requiresAccessibility && !e.isAccessible) {
        // ignore not accessible edges if we're looking for an accessible route
        continue;
      }

      // This code section does improve performance by about 10-20%, but comes at a cost
      // of making the caching unusable in some cases
      // if ((options.minOrd !== undefined && nodes[e.dst].ordinal < options.minOrd) ||
      //   (options.maxOrd !== undefined && nodes[e.dst].ordinal > options.maxOrd) ||
      //   (options.structureId !== undefined && nodes[e.dst].structureId !== options.structureId))
      //   continue

      const weight = getEdgeTraversalTime(e);

      const securityLanesById = securityLanesMap as Record<string, AnyRecord>;
      if (e.o && securityLanesById[e.o]) {
        e.securityLane = securityLanesById[e.o];
        const { type, id } = securityLanesById[e.o];
        const securityLanesIds = rPath(['selectedSecurityLanes', type], options) as string[] | undefined;
        if (securityLanesIds && !securityLanesIds.includes(id)) {
          continue;
        }
      }

      if (cost[e.dst] === undefined) {
        prev[e.dst] = node;
        cost[e.dst] = ccost + weight;
        visitQueue.offerWithPriority(e.dst, ccost + weight); // add node to the toCheck array
      } else if (cost[e.dst] > ccost + weight) {
        // is this a shorter path? Relaxation...
        // if so, update the cost and parent
        cost[e.dst] = ccost + weight;
        prev[e.dst] = node;
        visitQueue.raisePriority(e.dst, ccost + weight);
      }
    }
    visited[node.id] = true; // we have now been selected
  }

  if (!visited[end.id]) {
    // if we never found our endpoint, it was inaccessible
    return null;
  }

  // build the path and return it
  const path = [];
  let node: NavNode | undefined = end;
  while (node) {
    path.push(node);
    node = prev[node.id];
  }
  return path.reverse();
}

function geohashSearch(floorId: string, geohash: string, geoDb: GeoDb, size: number) {
  const geohashPrefix = geohash.substr(0, size);

  const searchGeos = [];
  searchGeos.push(floorId + ':' + calculateAdjacent(calculateAdjacent(geohashPrefix, 'top'), 'left'));
  searchGeos.push(floorId + ':' + calculateAdjacent(geohashPrefix, 'top'));
  searchGeos.push(floorId + ':' + calculateAdjacent(calculateAdjacent(geohashPrefix, 'top'), 'right'));
  searchGeos.push(floorId + ':' + calculateAdjacent(geohashPrefix, 'left'));
  searchGeos.push(floorId + ':' + geohashPrefix);
  searchGeos.push(floorId + ':' + calculateAdjacent(geohashPrefix, 'right'));
  searchGeos.push(floorId + ':' + calculateAdjacent(calculateAdjacent(geohashPrefix, 'bottom'), 'left'));
  searchGeos.push(floorId + ':' + calculateAdjacent(geohashPrefix, 'bottom'));
  searchGeos.push(floorId + ':' + calculateAdjacent(calculateAdjacent(geohashPrefix, 'bottom'), 'right'));

  const nodes: NavNode[] = [];
  for (let i = 0; i < searchGeos.length; i++) {
    const nodesFound = geoDb[searchGeos[i]];
    if (nodesFound) {
      for (let j = 0; j < nodesFound.length; j++) {
        nodes.push(nodesFound[j]);
      }
    }
  }

  return nodes;
}

function findNodesByGeohash(floorId: string, geohash: string, geoDb: GeoDb) {
  let foundNodes = geohashSearch(floorId, geohash, geoDb, 8);
  if (foundNodes.length > 0) {
    return foundNodes;
  }

  // broaden our search a bit and try again...
  foundNodes = geohashSearch(floorId, geohash, geoDb, 7);
  if (foundNodes.length > 0) {
    return foundNodes;
  }

  // give up and let someone else try...
  return null;
}

/**
 * @param floorId
 * @param lat
 * @param lng
 * @param geoDb
 * @param {Object<string, NavNode>} nodes - dictionary of node id to node object
 * @return {NavNode} - node that is on the same floor and closest to the lat,lng point
 */
function findClosestNodeByFloor(floorId: string, lat: number, lng: number, geoDb: GeoDb, nodes: NodesMap) {
  const hashedNodes = findNodesByGeohash(floorId, encode(lat, lng), geoDb);
  const cnodes: NavNode[] = hashedNodes ?? [findClosestNodeByFloor2(floorId, lat, lng, nodes)];

  const nodeWithDistance: Array<[NavNode, number]> = [];
  for (let i = 0; i < cnodes.length; i++) {
    // Attached distance to each node from the origin.
    const distance = getGeoDistance(lat, lng, cnodes[i].lat, cnodes[i].lng);
    nodeWithDistance.push([cnodes[i], distance]);
  }
  // todo do not sort, just find min node
  // Sort by distance.
  nodeWithDistance.sort(function (a, b) {
    return a[1] - b[1];
  });
  const nodesSortedByDistance: NavNode[] = [];
  for (let i = 0; i < nodeWithDistance.length; i++) {
    const nodeWithDistanceEntry = nodeWithDistance[i];
    if (nodeWithDistanceEntry) {
      nodesSortedByDistance.push(nodeWithDistanceEntry[0]);
    }
  }

  return nodesSortedByDistance[0];
}

// A slightly slower alternative to findClosestNode (an experiment - its simpler, but slower)
function findClosestNodeByFloor2(floorId: string, lat: number, lng: number, nodes: NodesMap) {
  const floorNodes: Array<[NavNode, number]> = Object.values(nodes)
    .filter(n => n.floorId === floorId)
    .map(n => [n, getGeoDistance(n.lat, n.lng, lat, lng)] as [NavNode, number]);
  if (!floorNodes.length) {
    throw Error(`findClosestNodeByFloor2 found no nodes on floor ${floorId}`);
  }
  return selectShortest(floorNodes);
}

export function findClosestNodeByOrdinal(ord: number, lat: number, lng: number, nodes: NodesMap) {
  const ordNodes: Array<[NavNode, number]> = Object.values(nodes)
    .filter(n => n.ordinal === ord)
    .map(n => [n, getGeoDistance(n.lat, n.lng, lat, lng)] as [NavNode, number]);
  if (!ordNodes.length) {
    throw Error(`findClosestNodeByOrdinal found no nodes on ordinal ${ord}`);
  }
  return selectShortest(ordNodes);
}

// companion to above function - it returns closest node
// ar is 2-dim array - ar[i][0] = node, ar[i][1] = distance
// TODO: use this approach in findClosestNode - no need to sort first!
function selectShortest(ar: Array<[NavNode, number]>) {
  let shortest = ar[0];
  for (let i = 1; i < ar.length; i++) {
    const current = ar[i];
    if (current && current[1] < shortest[1]) {
      shortest = current;
    }
  }

  return shortest[0];
}
